/**
 * High-performance image compression and universal format converter for mobile and desktop browsers.
 * Accepts any image format: HEIC, HEIF, WebP, AVIF, JPEG, PNG, GIF, BMP, TIFF, SVG, etc.
 * Converts and optimizes images so they fit well within Firestore's document limits,
 * while maintaining crisp visual clarity and fast loading.
 */

export interface CompressionOptions {
  maxDimension?: number;
  quality?: number;
  targetMime?: string;
}

/**
 * Checks if a file or blob is an image by MIME type or extension.
 */
export function isImageFile(file: File | Blob): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  if ('name' in file && typeof (file as File).name === 'string') {
    const ext = (file as File).name.split('.').pop()?.toLowerCase() || '';
    return [
      'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'avif', 'gif', 'bmp',
      'tiff', 'tif', 'svg', 'ico', 'jfif', 'dng', 'raw', 'cr2', 'nef', 'arw'
    ].includes(ext);
  }
  return false;
}

/**
 * Checks if a file is in Apple HEIC or HEIF format.
 */
export function isHeicFile(file: File | Blob): boolean {
  if (file.type === 'image/heic' || file.type === 'image/heif') return true;
  if ('name' in file && typeof (file as File).name === 'string') {
    const ext = (file as File).name.split('.').pop()?.toLowerCase() || '';
    return ext === 'heic' || ext === 'heif';
  }
  return false;
}

/**
 * Converts HEIC/HEIF files to standard JPEG Blob using heic2any.
 */
async function convertHeicIfNeeded(file: File | Blob): Promise<Blob> {
  if (!isHeicFile(file)) return file;
  try {
    const heic2anyModule = await import('heic2any');
    const heic2any: any = (heic2anyModule as any).default || heic2anyModule;
    const conversionResult = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.82,
    });
    return Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
  } catch (err) {
    console.warn('HEIC conversion via heic2any failed or unsupported, trying fallback:', err);
    return file;
  }
}

/**
 * Reads a File/Blob directly into a Data URL (base64).
 */
function readFileAsDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

/**
 * Compresses an image File or Blob into an optimized base64 Data URL.
 * Supports any photograph format (HEIC, HEIF, WebP, AVIF, JPG, PNG, GIF, BMP, TIFF, SVG).
 */
export async function compressImageFile(
  rawFile: File | Blob,
  options: CompressionOptions = {}
): Promise<string> {
  const {
    maxDimension = 1280,
    quality = 0.76,
    targetMime = 'image/jpeg',
  } = options;

  // 1. Convert HEIC/HEIF if needed
  let file = rawFile;
  try {
    file = await convertHeicIfNeeded(rawFile);
  } catch (err) {
    console.warn('HEIC pre-conversion warning:', err);
  }

  // 2. If it's an SVG or animated GIF, or not detected as an image, read directly
  if (
    file.type === 'image/svg+xml' ||
    file.type === 'image/gif' ||
    (!isImageFile(file) && !file.type.startsWith('image/'))
  ) {
    return readFileAsDataUrl(file);
  }

  // 3. Attempt modern Image decoding and canvas compression
  return new Promise<string>(async (resolve) => {
    let objectUrl = '';
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      // Direct reader fallback
      const dataUrl = await readFileAsDataUrl(file);
      resolve(dataUrl);
      return;
    }

    // Try standard HTML Image
    const img = new Image();

    const cleanupAndFallback = async () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      // Try createImageBitmap fallback
      try {
        if ('createImageBitmap' in window) {
          const bitmap = await createImageBitmap(file);
          let { width, height } = bitmap;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, width);
          canvas.height = Math.max(1, height);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(bitmap, 0, 0, width, height);
            const dataUrl = canvas.toDataURL(targetMime, quality);
            bitmap.close();
            resolve(dataUrl);
            return;
          }
        }
      } catch (bitmapErr) {
        console.warn('createImageBitmap fallback also failed:', bitmapErr);
      }

      // Final bulletproof fallback: read as Data URL directly
      try {
        const directDataUrl = await readFileAsDataUrl(file);
        resolve(directDataUrl);
      } catch (readErr) {
        console.error('Final direct read failed:', readErr);
        resolve('');
      }
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        let { width, height } = img;

        if (width <= 0 || height <= 0) {
          cleanupAndFallback();
          return;
        }

        // Calculate scaled dimensions
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, width);
        canvas.height = Math.max(1, height);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanupAndFallback();
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw image on white background for JPEG transparency safety
        if (targetMime === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to optimized data URL
        const compressedDataUrl = canvas.toDataURL(targetMime, quality);
        resolve(compressedDataUrl);
      } catch (err) {
        console.warn('Canvas draw failed, falling back to direct reader:', err);
        cleanupAndFallback();
      }
    };

    img.onerror = () => {
      cleanupAndFallback();
    };

    img.src = objectUrl;
  });
}

/**
 * Optimizes a batch of photos dynamically so the total combined size
 * is strictly guaranteed to remain below a safe Firestore budget.
 */
export async function compressBatchPhotos(
  files: File[],
  maxTotalBudgetKB: number = 750
): Promise<string[]> {
  const count = files.length;
  if (count === 0) return [];

  // Calculate target parameters per image based on batch size
  let maxDimension = 1280;
  let quality = 0.78;

  if (count > 8) {
    maxDimension = 800;
    quality = 0.65;
  } else if (count > 4) {
    maxDimension = 960;
    quality = 0.70;
  } else if (count > 2) {
    maxDimension = 1100;
    quality = 0.74;
  }

  const results = await Promise.all(
    files.map((file) => compressImageFile(file, { maxDimension, quality }))
  );

  return results.filter((url) => !!url);
}
